'use client';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui';
import type { JSX } from 'react';
import { LazyLottie } from '../lazy-lottie';

interface IConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  preventAutoClose?: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  pendingLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
  isPending?: boolean;
  animationSrc?: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  pendingLabel,
  cancelLabel = 'Cancel',
  variant = 'default',
  isPending = false,
  animationSrc,
  preventAutoClose = false,
}: IConfirmDialogProps): JSX.Element {
  const confirmButtonClass =
    variant === 'destructive'
      ? 'border border-destructive bg-transparent text-destructive hover:bg-destructive hover:text-white'
      : '';

  function handleClick(e: React.MouseEvent): void {
    if (preventAutoClose) {
      e.preventDefault();
    }
    onConfirm();
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader className="flex flex-col items-center text-center">
          {animationSrc && (
            <div className="h-48 w-48">
              <LazyLottie src={animationSrc} className="h-full w-full" />
            </div>
          )}
          <AlertDialogTitle className="text-foreground">{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="sm:justify-between">
          <AlertDialogCancel disabled={isPending} className="text-foreground">
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleClick}
            disabled={isPending}
            className={confirmButtonClass}
          >
            {isPending ? (pendingLabel ?? confirmLabel) : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
